import { Chess } from 'chess.js';
import chapters from './lessons.json';
export { chapters };
export const lessons = chapters.flatMap(chapter => chapter.lessons.map(lesson => ({ ...lesson, chapter: chapter.name, chapterId: chapter.id })));
export const names = { r: 'rook', b: 'bishop', q: 'queen', n: 'knight', k: 'king', p: 'pawn' };
export const squares = Array.from({ length: 64 }, (_, i) => 'abcdefgh'[i % 8] + (8 - Math.floor(i / 8)));
export const colour = piece => piece === piece.toUpperCase() ? 'w' : 'b';
export const parsePosition = text => Object.fromEntries(text.split(/\s+/).filter(Boolean).map(token => [token.slice(1), token[0]]));
export function toFen(board, turn = 'w', rights = '-', ep = '-') {
  const rows = Array.from({ length: 8 }, (_, row) => {
    let line = '', empty = 0;
    for (let file = 0; file < 8; file++) {
      const piece = board['abcdefgh'[file] + (8 - row)];
      if (!piece) empty++;
      else { if (empty) line += empty; empty = 0; line += piece; }
    }
    return line + (empty || '');
  });
  return `${rows.join('/')} ${turn} ${rights} ${ep} 0 1`;
}
function exerciseMoves(board, from) {
  const piece = board[from];
  if (!piece) return [];
  const own = colour(piece), type = piece.toLowerCase(), f = from.charCodeAt(0) - 97, r = +from[1];
  const result = [];
  const add = (x, y) => {
    if (x < 0 || x > 7 || y < 1 || y > 8) return false;
    const to = 'abcdefgh'[x] + y, target = board[to];
    if (target && colour(target) === own) return false;
    if (target?.toLowerCase() === 'k') return false;
    result.push({ from, to, captured: target?.toLowerCase(), promotion: type === 'p' && (y === 1 || y === 8) ? 'q' : undefined });
    return !target;
  };
  if (type === 'p') {
    const dir = own === 'w' ? 1 : -1;
    if (r + dir >= 1 && r + dir <= 8 && !board['abcdefgh'[f] + (r + dir)]) {
      add(f, r + dir);
      if (r === (own === 'w' ? 2 : 7) && !board['abcdefgh'[f] + (r + 2 * dir)]) add(f, r + 2 * dir);
    }
    for (const x of [f - 1, f + 1]) {
      const target = board['abcdefgh'[x] + (r + dir)];
      if (target && colour(target) !== own) add(x, r + dir);
    }
  } else if (type === 'n') {
    [[1,2],[2,1],[-1,2],[-2,1],[1,-2],[2,-1],[-1,-2],[-2,-1]].forEach(([x,y]) => add(f+x,r+y));
  } else {
    const dirs = type === 'r' ? [[1,0],[-1,0],[0,1],[0,-1]] : type === 'b' ? [[1,1],[-1,1],[1,-1],[-1,-1]] : [[1,0],[-1,0],[0,1],[0,-1],[1,1],[-1,1],[1,-1],[-1,-1]];
    dirs.forEach(([x,y]) => { for (let n=1;n<=(type === 'k' ? 1 : 7);n++) if (!add(f+x*n,r+y*n)) break; });
  }
  return result;
}
export function legalMoves(lesson, board, from) {
  if (!board[from] || colour(board[from]) !== lesson.turn) return [];
  if (!lesson.fullRules) return exerciseMoves(board, from);
  try { return new Chess(toFen(board, lesson.turn, lesson.rights, lesson.ep)).moves({ square: from, verbose: true }); }
  catch { return []; }
}
export function playMove(lesson, board, from, to, promotion = 'q') {
  const legal = legalMoves(lesson, board, from).find(move => move.to === to && (!lesson.fullRules || !move.promotion || move.promotion === promotion));
  if (!legal) return null;
  if (lesson.fullRules) {
    const game = new Chess(toFen(board, lesson.turn, lesson.rights, lesson.ep));
    const move = game.move({ from, to, promotion });
    const next = {};
    squares.forEach(square => { const p = game.get(square); if (p) next[square] = p.color === 'w' ? p.type.toUpperCase() : p.type; });
    return { board: next, move };
  }
  const next = { ...board }; delete next[from];
  next[to] = legal.promotion ? (lesson.turn === 'w' ? promotion.toUpperCase() : promotion) : board[from];
  return { board: next, move: legal };
}
export function goalReached(lesson, from, to) {
  return Array.isArray(lesson.goal) && (!lesson.goal.length || lesson.goal.includes(from + to));
}
export function invalidMoveMessage(lesson, board, from, to) {
  const piece = board[from];
  if (!piece) return 'Tap one of your pieces first, then tap where you want it to go.';
  if (board[to] && colour(board[to]) === colour(piece)) return 'That is your own piece. You cannot capture it or land on its square.';
  const type = piece.toLowerCase();
  if (type === 'k') return 'Your king needs a safe square, one step away. It cannot move into an attack. Tap Show me for help.';
  if (type === 'p') return 'A pawn moves straight forwards into an empty square. It captures one square diagonally forwards. It cannot go backwards or jump.';
  if (type === 'n') return 'Try an L: two squares one way, then one to the side. Your knight can jump, but must land on an available square.';
  return `Your ${names[type]} moves ${type === 'r' ? 'straight' : type === 'b' ? 'diagonally' : 'straight or diagonally'}. It cannot jump over pieces. Follow a clear path.`;
}
// Verified lesson actions are deterministic. Open questions use the authenticated Hood service.
export function resolveQuestion(text, lesson) {
  const q = text.toLowerCase().replace(/[’']/g, '');
  let id = null;
  const mentioned = Object.values(names).filter(name => new RegExp(`\\b${name}s?\\b`).test(q));
  const topic = mentioned.length === 1 ? mentioned[0] : lesson.chapterId;
  if (/en passant/.test(q)) id='08-09';
  else if (/castle|castling/.test(q)) id='08-01';
  else if (/stalemate/.test(q)) id='07-10';
  else if (/check\s*mate|check-mate|\bmate\b/.test(q)) id='07-06';
  else if (/\bcheck\b/.test(q)) id='07-01';
  else if (/promotion|promote|pawn.*end/.test(q)) id='06-09';
  else if (/why.*(two|2).*arrow|corner/.test(q)) id=lesson.chapterId==='knight'?'04-08':'01-06';
  else if (/jump|over.*(piece|pawn)/.test(q)) id=({knight:'04-05',queen:'03-09',bishop:'02-07',pawn:'06-05',king:'05-04'}[topic]||'01-07');
  else if (/(take|capture).*(queen)/.test(q) && lesson.chapterId==='checkmate') id='07-08';
  else if (/escape|run away/.test(q) && lesson.chapterId==='checkmate') id='07-07';
  else if (/block/.test(q) && lesson.chapterId==='checkmate') id='07-09';
  else if (/backward|go back/.test(q) && lesson.chapterId==='pawn') id='06-04';
  else if (/diagonal/.test(q) && lesson.chapterId==='rook') id='01-05';
  else {
    const pieces=[['rook','01'],['bishop','02'],['queen','03'],['knight','04'],['king','05'],['pawn','06']];
    const match=pieces.find(([name])=>new RegExp(`\\b${name}s?\\b`).test(q));
    if (match && /move|work|how|show|teach|learn|practi|what/.test(q)) {
      const capture=/take|capture/.test(q);
      id=match[1]+'-'+(capture?({rook:'08',bishop:'08',queen:'08',knight:'06',king:'07',pawn:'06'}[match[0]]):(match[0]==='pawn'?'01':'02'));
    }
  }
  return id ? lessons.findIndex(item=>item.id===id) : -1;
}
export function readProgress(storage, key) {
  try {
    const saved=JSON.parse(storage.getItem(key));
    if (!saved || saved.version!==1) return { index:0, completed:[] };
    return { index:Math.max(0,Math.min(lessons.length-1,Number.isInteger(saved.index)?saved.index:0)), completed:Array.isArray(saved.completed)?saved.completed.filter(id=>lessons.some(l=>l.id===id)):[] };
  } catch { return { index:0, completed:[] }; }
}
